import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { getWeekNumber } from 'helpers/dateTimes';
import { DaySelector } from './DaySelector';
import { TotadminPermission } from 'data/__generated__';

interface DeliveryPlanningFiltersProps {
    selectedDate: Date;
    selectedDays: boolean[];
    hasChanges: boolean;
    onDateChange: (date: Date) => void;
    onDaySelect: (dayIndex: number) => void;
    // Optional explicit save handler — when provided, the Enregistrer button does NOT submit the
    // surrounding form (used by the mini planning embedded inside the Site Details form).
    onSave?: () => void;
    saveLoading?: boolean;
    showBackButton?: boolean;
}

export const DeliveryPlanningFilters: React.FC<DeliveryPlanningFiltersProps> = ({
    selectedDate,
    selectedDays,
    hasChanges,
    onDateChange,
    onDaySelect,
    onSave,
    saveLoading = false,
    showBackButton = true,
}) => {
    const currentWeekNumber = getWeekNumber(selectedDate);
    const previousWeekNumber = getWeekNumber(new Date(selectedDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    const nextWeekNumber = getWeekNumber(new Date(selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000));

    const handlePreviousWeekClick = () => {
        const previousWeek = new Date(selectedDate);
        previousWeek.setDate(previousWeek.getDate() - 7);
        onDateChange(previousWeek);
    };

    const handleNextWeekClick = () => {
        const nextWeek = new Date(selectedDate);
        nextWeek.setDate(nextWeek.getDate() + 7);
        onDateChange(nextWeek);
    };

    return (
        <FiltersContainer>
            <WeekNavigationContainer>
                <WeekNavigationButton type="button" onClick={handlePreviousWeekClick} title="Semaine précédente">
                    <FaChevronLeft />
                </WeekNavigationButton>
                <ThreeWeeksDisplay>
                    <WeekInfo $isCurrent={false} onClick={handlePreviousWeekClick}>
                        S{previousWeekNumber}
                    </WeekInfo>
                    <WeekInfo $isCurrent>S{currentWeekNumber}</WeekInfo>
                    <WeekInfo $isCurrent={false} onClick={handleNextWeekClick}>
                        S{nextWeekNumber}
                    </WeekInfo>
                </ThreeWeeksDisplay>
                <WeekNavigationButton type="button" onClick={handleNextWeekClick} title="Semaine suivante">
                    <FaChevronRight />
                </WeekNavigationButton>
            </WeekNavigationContainer>
            <TotemDatePicker
                selected={selectedDate}
                onChange={(date) => {
                    onDateChange(date || selectedDate);
                }}
            />
            <TotemPrimaryButton
                type={onSave ? 'button' : 'submit'}
                onClick={onSave}
                disabled={!hasChanges || saveLoading}
                data-test="delivery-planning-save"
                requiredPermissions={[TotadminPermission.DeliveryPlanningManage]}
            >
                {saveLoading ? <Loader size="18px" mode={LoaderModeType.Spin} /> : 'Enregistrer'}
            </TotemPrimaryButton>

            {showBackButton && (
                <Link to="/sites">
                    <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                </Link>
            )}

            <DaySelector selectedDays={selectedDays} onDaySelect={onDaySelect} />
        </FiltersContainer>
    );
};

const FiltersContainer = styled.div`
    display: flex;
    align-items: center;
    padding: 0 20px;
    flex-shrink: 0;

    & > :not(:first-child) {
        margin-left: 20px;
    }
`;

const WeekNavigationContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const WeekNavigationButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    cursor: pointer;
`;

const ThreeWeeksDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    min-width: 200px;
`;

const WeekInfo = styled.div<{ $isCurrent: boolean }>`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    flex: 1;
    cursor: ${({ $isCurrent }) => ($isCurrent ? 'default' : 'pointer')};

    ${({ $isCurrent }) =>
        $isCurrent
            ? `
        background-color: #2196F3;
        color: white;
    `
            : `
        background-color: #f5f5f5;
        color: #666;
        
        &:hover {
            background-color: #e8e8e8;
            color: #333;
        }
    `}
`;
